import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiCard,
  MiContentDrawer,
  MiDetailFields,
  MiIcon,
  MiInputTextbox,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { mdiHomeOutline, mdiInfinity } from "@mdi/js";
import { Box, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import { useContactRelationshipDrawerViewModel } from "./useContactRelationshipDrawerViewModel";

const intialValues = {
  label: "",
  canBePrimary: true,
  max: 1,
};

const SELECTION_TYPES = {
  UNLIMITED: "unlimited",
  SPECIFIC: "specific",
};

export const ContactRelationshipDrawer = ({
  dependentType,
  editMode = false,
  relationship,
  open = false,
  onClose = () => null,
  onSave = () => null,
}) => {
  const { classes } = useStyles();
  const [selection, setSelection] = useState(SELECTION_TYPES.SPECIFIC);

  useEffect(() => {
    if (relationship?.max === -1) {
      setSelection(SELECTION_TYPES.UNLIMITED);
    } else {
      setSelection(SELECTION_TYPES.SPECIFIC);
    }
  }, [relationship]);
  const { isLoading, createRelationship, updateRelationship } =
    useContactRelationshipDrawerViewModel();
  const fields = [
    {
      label: "Relationship",
      name: "label",
      required: !editMode,
      width: "100%",
      validationRule: z
        .string({
          required_error: "Relationship is required",
          invalid_type_error: "Relationship must be a string",
        })
        .min(1, { message: "Relationship must be at least one character long" })
        .refine(
          (val) => {
            return !/\d/.test(val);
          },
          {
            message: "Relationship cannot contain any numbers",
          }
        ),
      readOnly: editMode,
    },
  ];
  const maxFieldValidation = {
    name: "max",
    validationRule: z
      .union([
        z.literal(-1),
        z.string().regex(/^\d+$/, "Must be a positive number"),
        z.number().int().positive(),
      ])
      .refine((val) => {
        const num = typeof val === "string" ? parseInt(val, 10) : val;
        return num === -1 || num > 0;
      }, "Must be a positive number"),
  };

  const edit = useEdit(relationship || intialValues, [
    ...fields,
    maxFieldValidation,
  ]);

  const handleSave = async () => {
    const payload = {
      ...edit.getValue(),
      dependentType,
    };
    const newRelationship = editMode
      ? await updateRelationship(payload)
      : await createRelationship(payload);

    if (!newRelationship) {
      return;
    }
    edit.reset();
    onSave();
  };

  const drawerActions = [
    {
      name: "Cancel",
      variant: BUTTON_VARIANTS.TEXT,
      size: "medium",
      color: BUTTON_COLORS.BLACK,
      onClick: () => {
        edit.reset();
        onClose();
      },
    },
    {
      name: `${editMode ? "Update" : "Create"} Relationship`,
      variant: BUTTON_VARIANTS.CONTAINED,
      size: "medium",
      disabled: !edit.isValid() || !edit.isDirty(),
      onClick: handleSave,
    },
  ];

  return (
    <MiContentDrawer
      variant="temporary"
      open={open}
      title={editMode ? relationship?.label : "Create New Relationship"}
      onClose={() => {
        edit.reset();
        onClose();
      }}
      width={600}
      actions={drawerActions}
      isLoading={isLoading}
    >
      <Box className={classes.container}>
        <MiCard
          title="Relationship Details"
          leftIcon={mdiHomeOutline}
          data-testid="relationship-fields"
        >
          <Box
            component="form"
            className={classes.relationshipDetailsfeildsContaner}
          >
            <MiDetailFields detailFields={fields} edit={edit} />
            <Box px={2}>
              <Typography color="text.secondary" variant="body2">
                Choose unlimited or specify a count number
              </Typography>
              <RadioGroup
                value={selection}
                onChange={(e) => setSelection(e.target.value)}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    my: 2,
                    "&:hover": {
                      bgcolor: "action.hover",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => {
                    setSelection(SELECTION_TYPES.UNLIMITED);
                    edit.update({ max: -1 });
                  }}
                >
                  <Box sx={{ p: 2, display: "flex" }}>
                    <Radio
                      value={SELECTION_TYPES.UNLIMITED}
                      sx={{ pt: 2 }}
                      disableRipple
                    />
                    <Box sx={{ flex: 1, ml: 1 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="600">
                            Unlimited
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No maximum limit
                          </Typography>
                        </Box>
                        <MiIcon
                          path={mdiInfinity}
                          size={1}
                          style={{ marginRight: 10 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    "&:hover": {
                      bgcolor: "action.hover",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => {
                    edit.update({ max: 1 });
                    setSelection(SELECTION_TYPES.SPECIFIC);
                  }}
                >
                  <Box sx={{ p: 2, display: "flex" }}>
                    <Radio
                      value={SELECTION_TYPES.SPECIFIC}
                      sx={{ pt: 2 }}
                      disableRipple
                    />
                    <Box sx={{ flex: 1, ml: 1 }}>
                      <Typography variant="body1" fontWeight="600">
                        Specific Count
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enter a number to specify the maximum count
                      </Typography>
                      {selection === SELECTION_TYPES.SPECIFIC && (
                        <Box mt={2}>
                          <MiInputTextbox
                            size="small"
                            error={edit?.errors?.max}
                            value={edit.getValue("max")}
                            placeholder="Enter count"
                            handleChange={(e) => {
                              edit.update({ max: e.target.value });
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: 150 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </RadioGroup>
            </Box>
          </Box>
        </MiCard>
      </Box>
    </MiContentDrawer>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    gap: 16,
    flex: 1,
    flexDirection: "column",
    borderRadius: 10,
  },
  relationshipDetailsfeildsContaner: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    marginTop: theme.spacing(3),
  },
  relationshipDetailSection: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
  },
}));
